import React, { useEffect, useRef, useState, useCallback, useLayoutEffect } from 'react';
import Portal from './Portal';
import './ColorPicker.css';

const ColorPicker = ({ color, setColor, id, pickerClassName = 'color-picker' }) => {
  const safeString = (c) => (typeof c === 'string' ? c.trim() : '');
  const clamp01 = (n) => (Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 1);

  const rgbToHex = useCallback(
    (r, g, b) =>
      '#' +
      [r, g, b]
        .map((x) => {
          const v = Math.min(255, Math.max(0, Math.round(x)));
          const hex = v.toString(16);
          return hex.length === 1 ? '0' + hex : hex;
        })
        .join(''),
    []
  );

  const hexToRgb = useCallback((hex) => {
    if (!hex || typeof hex !== 'string') return null;
    let c = hex.startsWith('#') ? hex.slice(1) : hex;
    if (c.length === 3) c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
    if (c.length !== 6) return null;
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    if (![r, g, b].every((n) => Number.isFinite(n))) return null;
    return [r, g, b];
  }, []);

  const parseColor = useCallback(
    (input) => {
      const c = safeString(input);
      if (!c) return { r: 0, g: 0, b: 0, a: 1, hasAlpha: false };

      if (c.startsWith('#')) {
        const rgb = hexToRgb(c);
        if (rgb) return { r: rgb[0], g: rgb[1], b: rgb[2], a: 1, hasAlpha: false };
        return { r: 0, g: 0, b: 0, a: 1, hasAlpha: false };
      }

      if (c.startsWith('rgba')) {
        const nums = c.match(/-?\d*\.?\d+/g);
        if (!nums || nums.length < 4) return { r: 0, g: 0, b: 0, a: 1, hasAlpha: false };
        const r = Number(nums[0]);
        const g = Number(nums[1]);
        const b = Number(nums[2]);
        const a = clamp01(Number(nums[3]));
        return {
          r: Number.isFinite(r) ? r : 0,
          g: Number.isFinite(g) ? g : 0,
          b: Number.isFinite(b) ? b : 0,
          a,
          hasAlpha: true,
        };
      }

      if (c.startsWith('rgb')) {
        const nums = c.match(/-?\d+/g);
        if (!nums || nums.length < 3) return { r: 0, g: 0, b: 0, a: 1, hasAlpha: false };
        const r = Number(nums[0]);
        const g = Number(nums[1]);
        const b = Number(nums[2]);
        return {
          r: Number.isFinite(r) ? r : 0,
          g: Number.isFinite(g) ? g : 0,
          b: Number.isFinite(b) ? b : 0,
          a: 1,
          hasAlpha: false,
        };
      }

      return { r: 0, g: 0, b: 0, a: 1, hasAlpha: false };
    },
    [hexToRgb]
  );

  const toToneRgbString = useCallback(
    (input) => {
      const { r, g, b, a, hasAlpha } = parseColor(input);
      const tone = hasAlpha ? clamp01(a) : 1;
      const rr = Math.round(r * tone);
      const gg = Math.round(g * tone);
      const bb = Math.round(b * tone);
      return `rgb(${rr}, ${gg}, ${bb})`;
    },
    [parseColor]
  );

  const toToneHex = useCallback(
    (input) => {
      const { r, g, b, a, hasAlpha } = parseColor(input);
      const tone = hasAlpha ? clamp01(a) : 1;
      return rgbToHex(r * tone, g * tone, b * tone);
    },
    [parseColor, rgbToHex]
  );

  const [currentColor, setCurrentColor] = useState(() => toToneRgbString(color || '#000000'));
  const [currentHexInputValue, setCurrentHexInputValue] = useState(() => toToneHex(color || '#000000'));

  const [showChooser, setShowChooser] = useState(false);
  const chooserRef = useRef(null);
  const pickerButtonRef = useRef(null);
  const canvasRef = useRef(null);

  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [currentThemeClass, setCurrentThemeClass] = useState('');

  const getActiveThemeClass = useCallback(() => {
    const bodyClassList = document.body.classList;
    if (bodyClassList.contains('dark-theme')) return 'dark-theme';
    if (bodyClassList.contains('light-theme')) return 'light-theme';
    return '';
  }, []);

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          setCurrentThemeClass(getActiveThemeClass());
        }
      });
    });

    observer.observe(document.body, { attributes: true });
    setCurrentThemeClass(getActiveThemeClass());

    return () => observer.disconnect();
  }, [getActiveThemeClass]);

  const rgbPresetColors = [
    [0, 0, 0],
    [0, 0, 128],
    [0, 0, 255],
    [100, 165, 255],
    [0, 255, 255],
    [0, 100, 0],
    [0, 255, 0],
    [128, 128, 128],
    [165, 42, 42],
    [255, 0, 0],
    [255, 165, 0],
    [255, 255, 0],
    [128, 0, 128],
    [255, 0, 255],
    [255, 128, 255],
    [255, 255, 255],
  ];

  const calculatePopupPosition = useCallback(() => {
    if (!pickerButtonRef.current || !chooserRef.current || !showChooser) return;

    const buttonRect = pickerButtonRef.current.getBoundingClientRect();
    const chooserRect = chooserRef.current.getBoundingClientRect();

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    let top = buttonRect.bottom + 8;
    let left = buttonRect.left;

    if (top + chooserRect.height > viewportHeight - 10) {
      top = buttonRect.top - chooserRect.height - 8;
      if (top < 10) top = buttonRect.bottom + 8;
    }

    if (left + chooserRect.width > viewportWidth - 10) {
      left = viewportWidth - chooserRect.width - 10;
      if (left < 10) left = 10;
    }

    if (left < 10) left = 10;

    setPopupPosition({ top, left });
  }, [showChooser]);

  useLayoutEffect(() => {
    if (!showChooser) return;

    const timeoutId = setTimeout(() => calculatePopupPosition(), 0);

    window.addEventListener('resize', calculatePopupPosition);
    window.addEventListener('scroll', calculatePopupPosition, true);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', calculatePopupPosition);
      window.removeEventListener('scroll', calculatePopupPosition, true);
    };
  }, [showChooser, calculatePopupPosition]);

  useEffect(() => {
    if (!canvasRef.current) return;

    const context = canvasRef.current.getContext('2d', { willReadFrequently: true });
    const width = context.canvas.width;
    const height = context.canvas.height;

    context.clearRect(0, 0, width, height);

    const gradient = context.createLinearGradient(0, 0, width, 0);
    gradient.addColorStop(0, '#FFFFFF');
    gradient.addColorStop(0.5, currentColor);
    gradient.addColorStop(1, '#000000');

    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);

    const canvasClickHandler = (e) => {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pixel = context.getImageData(x, 0, 1, 1).data;

      const newColor = `rgb(${pixel[0]}, ${pixel[1]}, ${pixel[2]})`;
      setColor(newColor);
      setCurrentColor(newColor);
      setCurrentHexInputValue(rgbToHex(pixel[0], pixel[1], pixel[2]));
    };

    canvasRef.current.addEventListener('click', canvasClickHandler);
    return () => canvasRef.current?.removeEventListener('click', canvasClickHandler);
  }, [currentColor, setColor, rgbToHex]);

  useEffect(() => {
    const nextTone = toToneRgbString(color || '#000000');
    if (nextTone !== currentColor) {
      setCurrentColor(nextTone);
      setCurrentHexInputValue(toToneHex(color || '#000000'));
    }
  }, [color, currentColor, toToneRgbString, toToneHex]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        chooserRef.current &&
        !chooserRef.current.contains(event.target) &&
        pickerButtonRef.current &&
        !pickerButtonRef.current.contains(event.target)
      ) {
        setShowChooser(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleHexInputChange = (e) => {
    const hexInput = e.target.value;
    setCurrentHexInputValue(hexInput);

    if (/^#?([0-9A-Fa-f]{3}){1,2}$/i.test(hexInput)) {
      const rgb = hexToRgb(hexInput);
      if (rgb) {
        const newColor = `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
        setColor(newColor);
        setCurrentColor(newColor);
      }
    } else if (hexInput === '' || hexInput === '#') {
    }
  };

  const wrapperClass =
    pickerClassName === 'color-picker-fullwidth' ? 'cp-wrap is-full' : 'cp-wrap';

  return (
    <div className={wrapperClass}>
      <button
        ref={pickerButtonRef}
        id={`color-picker-${id}`}
        type="button"
        className={`cp-btn ${pickerClassName}`}
        style={{ '--cp-color': currentColor || '#000000' }}
        onClick={() => setShowChooser((prev) => !prev)}
        aria-label="Open color picker"
      />

      {showChooser && (
        <Portal className={currentThemeClass}>
          <div
            ref={chooserRef}
            id={`color-chooser-${id}`}
            className="cp-pop cp-pop--open"
            style={{
              top: popupPosition.top,
              left: popupPosition.left,
            }}
          >
            <div className="cp-head">
              <div className="cp-monitor" style={{ '--cp-current': currentColor || '#000000' }} />
              <div className="cp-meta">
                <div className="cp-metaTitle">Color</div>
                <div className="cp-metaSub">{currentHexInputValue}</div>
              </div>
            </div>

            <div className="cp-sliderWrap">
              <canvas className="cp-canvas" ref={canvasRef} width="280" height="20" />
            </div>

            <div className="cp-presets">
              {rgbPresetColors.map((c, idx) => {
                const presetRgb = `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
                const presetHex = rgbToHex(c[0], c[1], c[2]);

                const cur = parseColor(currentColor);
                const currentHex = rgbToHex(cur.r, cur.g, cur.b);

                const isSelected = currentHex.toLowerCase() === presetHex.toLowerCase();

                return (
                  <button
                    key={idx}
                    type="button"
                    className={`cp-preset ${isSelected ? 'is-selected' : ''}`}
                    style={{ '--cp-preset': presetRgb }}
                    onClick={() => {
                      setColor(presetRgb);
                      setCurrentColor(presetRgb);
                      setCurrentHexInputValue(presetHex);
                    }}
                    aria-label={`Preset ${presetHex}`}
                  />
                );
              })}
            </div>

            <div className="cp-hexRow">
              <label className="cp-hexLbl" htmlFor={`hex-input-${id}`}>
                Hex
              </label>
              <input
                id={`hex-input-${id}`}
                type="text"
                value={currentHexInputValue}
                onChange={handleHexInputChange}
                placeholder="#RRGGBB"
                className="add-column cp-hexInput"
                maxLength={7}
              />
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
};

export default ColorPicker;
